import Component from "@glimmer/component";
import { fn } from "@ember/helper";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import ReviewableFlagReason from "discourse/components/reviewable/flag-reason";
import ReviewableNoteForm from "discourse/components/reviewable/note-form";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import escape from "discourse/lib/escape";
import {
  penaltyIcon,
  penaltyPastTense,
} from "discourse/lib/reviewable-penalty";
import { sanitize } from "discourse/lib/text";
import { CLAIMED, UNCLAIMED } from "discourse/models/reviewable-history";
import DButton from "discourse/ui-kit/d-button";
import DDecoratedHtml from "discourse/ui-kit/d-decorated-html";
import DInterpolatedTranslation from "discourse/ui-kit/d-interpolated-translation";
import DUserLink from "discourse/ui-kit/d-user-link";
import dAvatar from "discourse/ui-kit/helpers/d-avatar";
import dFormatDate from "discourse/ui-kit/helpers/d-format-date";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";

/**
 * @typedef {{ id: number, username: string, avatar_template: string, name?: string | null }} ReviewableTimelineUser
 * @typedef {{ id: number, content: string, cooked?: string | null, created_at: string, updated_at: string, user?: ReviewableTimelineUser | null }} ReviewableNoteData
 * @typedef {{ type: string, date: string, user: ReviewableTimelineUser | null | undefined, icon: string, titleKey?: string, description?: import("@ember/template").TrustedHTML | null, score?: { count: number, type: string, title: string }, noteId?: number, canDelete?: boolean | null }} ReviewableTimelineEvent
 */
/**
 * Timeline component for reviewable items that displays chronological events
 * including flags, reviews, notes, and target post creation.
 *
 * @component ReviewableTimeline
 */
export default class ReviewableTimeline extends Component {
  @service currentUser;

  /**
   * Combines all timeline events from reviewable scores, histories, and the reviewable itself
   * and sorts them chronologically.
   *
   * @returns {ReviewableTimelineEvent[]} Array of timeline event objects sorted by date (newest first)
   */
  get timelineEvents() {
    const events = [];
    const reviewedEvents = new Map(); // Track reviewed events to prevent duplicates

    // Add target post creation event (when the original post was created)
    if (this.args.reviewable.target_created_at) {
      events.push({
        type: "target_created",
        date: this.args.reviewable.target_created_at,
        user: this.args.reviewable.target_created_by,
        icon: "pen-to-square",
        titleKey: "review.timeline.target_created_by",
      });
    }

    // Add target post deletion event (when the post was deleted)
    if (this.args.reviewable.target_deleted_at) {
      events.push({
        type: "target_deleted",
        date: this.args.reviewable.target_deleted_at,
        user: this.args.reviewable.target_deleted_by,
        icon: "trash-can",
        titleKey: "review.timeline.target_deleted_by",
      });
    }

    // Add flagging events from reviewable scores
    if (this.args.reviewable.reviewable_scores) {
      this.args.reviewable.reviewable_scores.forEach((score) => {
        // Build description for flagged event
        let flaggedDescription = "";
        if (score.reason) {
          // generated by the server
          flaggedDescription += score.reason;
        }
        if (score.context) {
          // user input
          flaggedDescription += `<p>${escape(score.context)}</p>`;
        }

        // Add conversation message if available
        if (
          score.reviewable_conversation &&
          score.reviewable_conversation.conversation_posts &&
          score.reviewable_conversation.conversation_posts.length > 0
        ) {
          const firstPost = score.reviewable_conversation.conversation_posts[0];
          flaggedDescription += `<p>${firstPost.excerpt} (<a href="${escape(
            score.reviewable_conversation.permalink
          )}">${i18n("review.timeline.view_conversation")}</a>)</p>`;
        }

        events.push({
          type: "flagged",
          date: score.created_at,
          user: score.user,
          icon: "flag",
          titleKey: "review.timeline.flagged_as_by",
          description: trustHTML(sanitize(flaggedDescription)),
          score: {
            count: 0,
            type: score.score_type.type,
            title: score.score_type.title,
          },
        });

        // Add separate reviewed event if both reviewed_by and reviewed_at are present
        if (score.reviewed_by && score.reviewed_at) {
          // Create a unique key for this reviewed event to prevent duplicates
          const reviewedKey = `${score.reviewed_by.id}-${score.reviewed_at}`;

          if (!reviewedEvents.has(reviewedKey)) {
            // Determine icon based on score status
            let reviewIcon;
            let titleKey;
            switch (score.status) {
              case 1: // approved
                reviewIcon = "check";
                titleKey = "review.timeline.approved_by";
                break;
              case 2: // rejected
                reviewIcon = "xmark";
                titleKey = "review.timeline.rejected_by";
                break;
              case 3: // ignored
                reviewIcon = "far-eye-slash";
                titleKey = "review.timeline.ignored_by";
                break;
              default:
                reviewIcon = "check"; // fallback
            }

            const reviewedEvent = {
              type: "reviewed",
              date: score.reviewed_at,
              user: score.reviewed_by,
              icon: reviewIcon,
              titleKey,
            };

            events.push(reviewedEvent);
            reviewedEvents.set(reviewedKey, reviewedEvent);
          }
        }
      });
    }

    this.args.reviewable.author_penalties?.forEach((penalty) => {
      if (!penalty.from_this_target || !penalty.applied_at) {
        return;
      }

      events.push({
        type: `author_${penaltyPastTense(penalty.kind)}`,
        date: penalty.applied_at,
        user: penalty.applied_by,
        icon: penaltyIcon(penalty.kind),
        titleKey: penalty.automatic
          ? `review.timeline.author_${penaltyPastTense(penalty.kind)}_automatically`
          : `review.timeline.author_${penaltyPastTense(penalty.kind)}_by`,
        description: penalty.reason
          ? trustHTML(sanitize(penalty.reason))
          : null,
      });
    });

    // Add notes events
    this.args.reviewable.reviewable_notes?.forEach((note) => {
      const date = note.created_at;
      events.push({
        type: "note",
        date,
        user: note.user,
        icon: "far-pen-to-square",
        titleKey: "review.timeline.note_added_by",
        description: trustHTML(note.cooked ?? `<p>${escape(note.content)}</p>`),
        noteId: note.id,
        canDelete:
          this.currentUser &&
          note.user &&
          (this.currentUser.id === note.user.id || this.currentUser.admin),
      });
    });

    this.args.historyEvents?.forEach((history) => {
      if (history.reviewable_history_type === CLAIMED) {
        events.push({
          type: "claimed",
          date: history.created_at,
          user: history.created_by,
          icon: "user-plus",
          titleKey: "review.timeline.claimed_by",
        });
      } else if (history.reviewable_history_type === UNCLAIMED) {
        events.push({
          type: "unclaimed",
          date: history.created_at,
          user: history.created_by,
          icon: "user-xmark",
          titleKey: "review.timeline.unclaimed_by",
        });
      }
    });

    return events.sort((a, b) => Date.parse(a.date) - Date.parse(b.date));
  }

  /**
   * Handles creation of a new note.
   *
   * @param {ReviewableNoteData} noteData - The created note data from the server
   */
  @action
  onNoteCreated(noteData) {
    // Ensure the note has a user object (fallback to current user if missing)
    if (!noteData.user && this.currentUser) {
      noteData.user = this.currentUser;
    }

    this.args.reviewable.reviewable_notes.push(noteData);
  }

  /**
   * Handles deletion of a note.
   *
   * @param {number} noteId - ID of the note to delete
   */
  @action
  async deleteNote(noteId) {
    try {
      await ajax(`/review/${this.args.reviewable.id}/notes/${noteId}`, {
        type: "DELETE",
      });

      const { reviewable } = this.args;
      reviewable.reviewable_notes = reviewable.reviewable_notes.filter(
        (note) => note.id !== noteId
      );
    } catch (error) {
      popupAjaxError(error);
    }
  }

  <template>
    <div class="reviewable-timeline">

      {{#if this.timelineEvents}}
        <div class="timeline-events">
          {{#each this.timelineEvents as |event|}}
            <div class="timeline-event">
              <div class="timeline-event__icon">
                {{dIcon event.icon}}
              </div>

              <div class="timeline-event__content">
                <div class="timeline-event__main">
                  <div class="timeline-event__text">
                    <div class="timeline-event__title">
                      <DInterpolatedTranslation
                        @key={{event.titleKey}}
                        as |Placeholder|
                      >
                        <Placeholder @name="flagReason">
                          <ReviewableFlagReason @score={{event.score}} />
                        </Placeholder>

                        <Placeholder @name="username">
                          {{dAvatar event.user imageSize="tiny"}}
                          <DUserLink
                            @user={{event.user}}
                          >{{event.user.username}}</DUserLink>
                        </Placeholder>

                        <Placeholder @name="relativeDate">
                          {{dFormatDate event.date format="medium"}}
                        </Placeholder>
                      </DInterpolatedTranslation>
                    </div>
                    {{#if event.description}}
                      <DDecoratedHtml
                        @className={{if
                          (eq event.type "note")
                          "timeline-event__description --note"
                          "timeline-event__description"
                        }}
                        @html={{event.description}}
                      />
                    {{/if}}
                  </div>

                  {{#if (and (eq event.type "note") event.canDelete)}}
                    <div class="timeline-event__actions">
                      <DButton
                        class="btn-transparent --danger timeline-event__delete-note btn-transparent"
                        @action={{fn this.deleteNote event.noteId}}
                        @icon="trash-can"
                        @title="review.notes.delete_note"
                      />
                    </div>
                  {{/if}}
                </div>

              </div>
            </div>
          {{/each}}
        </div>
      {{else}}
        <div class="timeline-empty">
          <div class="timeline-empty__icon">
            {{dIcon "clock"}}
          </div>
          <p class="timeline-empty__message">
            {{i18n "review.timeline.no_events"}}
          </p>
        </div>
      {{/if}}
      <ReviewableNoteForm
        @onNoteCreated={{this.onNoteCreated}}
        @reviewable={{@reviewable}}
      />
    </div>
  </template>
}
